import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  privateCaseCategories,
  companyCaseCategories,
  findCategory,
  findSubCategory
} from '../data/chatCaseCategories';

const API = '/api';

// Initial welcome message
const INITIAL_MESSAGE = {
  role: 'assistant',
  content: 'Hei! Jeg er **Kari** \ud83d\udc4b\n\nJeg er din AI-assistent og hjelper deg \u00e5 beskrive din juridiske situasjon.\n\nEr dette for deg personlig eller en bedrift?',
  timestamp: new Date(),
  quickReplies: [
    { icon: '\ud83d\udc64', text: 'Meg personlig', value: 'PERSON' },
    { icon: '\ud83c\udfec', text: 'Bedrift', value: 'COMPANY' }
  ]
};

/**
 * Custom hook for chat flow logic
 * Manages the conversation flow, category selection, and AI review
 */
export const useChatFlow = () => {
  // Core chat state
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [isTyping, setIsTyping] = useState(false);
  
  // Flow state
  const [clientType, setClientType] = useState(null);
  const [isExistingCustomer, setIsExistingCustomer] = useState(null);
  const [showCompanyLookup, setShowCompanyLookup] = useState(false);
  const [companyLookupInput, setCompanyLookupInput] = useState('');
  const [caseCategory, setCaseCategory] = useState(null);
  const [caseSubCategory, setCaseSubCategory] = useState(null);
  const [showInput, setShowInput] = useState(false);
  const [caseSummary, setCaseSummary] = useState(null);
  const [isInReviewMode, setIsInReviewMode] = useState(false);

  // Helper to add messages
  const addMessage = useCallback((role, content, quickReplies = null) => {
    setMessages(prev => [...prev, {
      role,
      content,
      timestamp: new Date(),
      quickReplies,
      id: Date.now()
    }]);
  }, []);

  // Handle quick reply selection (PERSON/COMPANY, YES/NO)
  const handleQuickReply = useCallback(async (value) => {
    if (value === 'PERSON' || value === 'COMPANY') {
      setClientType(value);
      addMessage('user', value === 'PERSON' ? 'Meg personlig' : 'Bedrift');
      
      if (value === 'COMPANY') {
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          addMessage('assistant', 'Perfekt! \ud83c\udfec\n\nEr dere allerede kunde hos oss?', [
            { type: 'existing', icon: '\u2714\ufe0f', text: 'Ja, vi er allerede kunde', value: 'YES' },
            { type: 'existing', icon: '\u2716\ufe0f', text: 'Nei, vi er en ny bedrift', value: 'NO' }
          ]);
        }, 1200);
      } else {
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          addMessage('assistant', 'Perfekt! \ud83d\udccb\n\nHva slags juridisk sak gjelder dette?',
            privateCaseCategories.map(cat => ({
              type: 'category', id: cat.id, icon: cat.icon, text: cat.label, desc: cat.desc, value: cat.id
            }))
          );
        }, 1200);
      }
    } else if (value === 'YES' || value === 'NO') {
      setIsExistingCustomer(value === 'YES');
      addMessage('user', value === 'YES' ? '\u2714\ufe0f Ja, vi er allerede kunde' : '\u2716\ufe0f Nei, vi er en ny bedrift');
      
      if (value === 'YES') {
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          addMessage('assistant', 'Flott! \ud83d\udd0d\n\nVennligst skriv inn bedriftens e-post eller organisasjonsnummer, s\u00e5 finner vi dere i systemet.');
          setShowCompanyLookup(true);
        }, 1200);
      } else {
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          addMessage('assistant', 'Perfekt! \ud83c\udfec\n\nHvilken type bedriftsjuridisk sak gjelder dette?',
            companyCaseCategories.map(cat => ({
              type: 'category', id: cat.id, icon: cat.icon, text: cat.label, desc: cat.desc, value: cat.id
            }))
          );
        }, 1200);
      }
    }
  }, [addMessage]);

  // Handle category selection
  const handleCategorySelect = useCallback((categoryId) => {
    const category = findCategory(categoryId, clientType);
    setCaseCategory(categoryId);
    addMessage('user', `${category.icon} ${category.label}`);
    
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      
      if (clientType === 'COMPANY') {
        addMessage('assistant', `Flott! Jeg forst\u00e5r at dette gjelder **${category.label.toLowerCase()}**. \ud83d\udcac\n\nBeskriv gjerne situasjonen deres med egne ord. Jo mer detaljer, desto bedre kan advokaten hjelpe.`);
        setShowInput(true);
        return;
      }
      
      if (category.subCategories && category.subCategories.length > 0) {
        addMessage('assistant', `Perfekt! For **${category.label}** - hva gjelder det mer spesifikt? \ud83c\udfaf`,
          category.subCategories.map(sub => ({
            type: 'subcategory', id: sub.id, icon: sub.icon, text: sub.label, desc: sub.desc, value: sub.id
          }))
        );
      } else {
        addMessage('assistant', `Flott! Jeg forst\u00e5r at dette gjelder **${category.label.toLowerCase()}**. \ud83d\udcac\n\nBeskriv gjerne situasjonen din med egne ord. Jo mer detaljer, desto bedre kan advokaten hjelpe.`);
        setShowInput(true);
      }
    }, 1200);
  }, [clientType, addMessage]);

  // Handle subcategory selection
  const handleSubCategorySelect = useCallback((subCategoryId) => {
    const subCategory = findSubCategory(caseCategory, subCategoryId, clientType);
    setCaseSubCategory(subCategoryId);
    addMessage('user', `${subCategory.icon} ${subCategory.label}`);
    
    const pronoun = clientType === 'PERSON' ? 'din' : 'deres';
    
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMessage('assistant', `Flott! N\u00e5 vet jeg at det gjelder **${subCategory.label.toLowerCase()}**. \ud83d\udcac\n\nBeskriv gjerne situasjonen ${pronoun} med egne ord. Jo mer detaljer, desto bedre kan advokaten hjelpe.`);
      setShowInput(true);
    }, 1500);
  }, [caseCategory, clientType, addMessage]);

  // Trigger AI review of user's description
  const triggerAIReview = useCallback(async (userMessage, uploadedFile) => {
    setIsTyping(true);
    try {
      const response = await axios.post(`${API}/intake/chat`, {
        conversation_history: messages.map(m => ({ role: m.role, content: m.content })),
        user_message: userMessage,
        client_type: clientType,
        case_category: caseCategory,
        case_subcategory: caseSubCategory,
        request_review: true,
        has_documents: uploadedFile !== null
      }, { timeout: 15000 });

      setTimeout(() => {
        setIsTyping(false);
        if (response?.data?.response) {
          setIsInReviewMode(true);
          setCaseSummary(response.data.summary);
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: response.data.response,
            timestamp: new Date(),
            quickReplies: [
              { type: 'action', icon: '\ud83d\udcdd', text: 'Legg til mer informasjon', value: 'ADD_MORE' },
              { type: 'action', icon: '\ud83d\udcce', text: 'Last opp vedlegg', value: 'UPLOAD_DOCS' },
              { type: 'action', icon: '\u27a1\ufe0f', text: 'G\u00e5 videre', value: 'CONTINUE' }
            ]
          }]);
        } else {
          addMessage('assistant', 'Beklager, noe gikk galt. Kan du pr\u00f8ve igjen?');
          toast.error('Uventet svar fra server');
        }
      }, 800);
    } catch (error) {
      console.error('Chat error:', error);
      setIsTyping(false);
      let errorMessage = 'Kunne ikke sende melding. Pr\u00f8v igjen.';
      if (error.code === 'ECONNABORTED') errorMessage = 'Foresp\u00f8rselen tok for lang tid. Pr\u00f8v igjen.';
      else if (!error.response) errorMessage = 'Ingen forbindelse til server. Sjekk internett.';
      addMessage('assistant', `\u26a0\ufe0f ${errorMessage}`);
      toast.error(errorMessage);
    }
  }, [messages, clientType, caseCategory, caseSubCategory, addMessage]);

  // Handle company lookup
  const handleCompanyLookup = useCallback(async (e) => {
    e?.preventDefault();
    if (!companyLookupInput.trim()) return;
    setShowCompanyLookup(false);
    addMessage('user', companyLookupInput);
    setIsTyping(true);

    try {
      const response = await axios.post(`${API}/tenant/lookup`, { identifier: companyLookupInput });
      if (response.data.found) {
        setIsTyping(false);
        addMessage('assistant', `\u2705 Velkommen tilbake, **${response.data.company_name}**!\n\nDu blir n\u00e5 sendt til deres Lexy Business-portal hvor dere kan opprette nye saker direkte.`);
        toast.success('Bedrift funnet! Omdirigerer...');
        setTimeout(() => { window.location.href = `/lexy-business?tenant=${response.data.tenant_id}`; }, 2000);
      } else {
        setIsTyping(false);
        addMessage('assistant', '\ud83d\udd0d Vi fant ikke bedriften i systemet.\n\nIngen problem! La oss fortsette som ny bedrift.',
          companyCaseCategories.map(cat => ({ type: 'category', id: cat.id, icon: cat.icon, text: cat.label, desc: cat.desc, value: cat.id }))
        );
        setIsExistingCustomer(false);
      }
    } catch (error) {
      console.error('Lookup error:', error);
      setIsTyping(false);
      addMessage('assistant', '\u26a0\ufe0f Kunne ikke s\u00f8ke etter bedrift. La oss fortsette som ny kunde.');
      toast.error('S\u00f8k feilet');
      setIsExistingCustomer(false);
    }
  }, [companyLookupInput, addMessage]);

  return {
    // State
    messages,
    isTyping,
    clientType,
    isExistingCustomer,
    showCompanyLookup,
    companyLookupInput,
    setCompanyLookupInput,
    caseCategory,
    caseSubCategory,
    showInput,
    setShowInput,
    caseSummary,
    isInReviewMode,
    setIsInReviewMode,
    
    // Actions
    addMessage,
    setMessages,
    setIsTyping,
    handleQuickReply,
    handleCategorySelect,
    handleSubCategorySelect,
    triggerAIReview,
    handleCompanyLookup
  };
};

export default useChatFlow;
